import { useCallback, useMemo } from 'react';

import { RemoveLocationButton, UpsertLocationButton } from 'contacts/components';
import {
  ContactGroupDetails,
  ContactGroupDetailsTab,
  MergeContactGroupsButton,
} from 'contacts/components/ContactGroupList';
import { ContactGroupType } from 'contacts/enums';
import { IContactGroup, IContactGroupAddress } from 'contacts/types';
import { AgGridServerTable } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useAgGridServerTableContext } from 'core/states';
import { ITableActionProps } from 'core/types';

import { MergeLocationsButton } from './SelectionActions';

interface IProps {
  selectedContactGroup: IContactGroup | null;
  setSelectedContactGroup: (contactGroup: IContactGroup | null) => void;
  contactGroupType: ContactGroupType;
}

export const LocationListTable = ({
  selectedContactGroup,
  setSelectedContactGroup,
  contactGroupType,
}: IProps) => {
  const { gridApi, selectedRows, updateRows } = useAgGridServerTableContext();

  const onEditContactGroupSuccess = (contactGroup: IContactGroup) => {
    setSelectedContactGroup(contactGroup);
    gridApi?.refreshServerSide({ purge: true });
  };

  const onMergeLocationsSuccess = useCallback(
    (updatedLocation: IContactGroupAddress) => {
      const removedLocation = selectedRows.find(
        (location) => location.addressId !== updatedLocation.addressId
      );

      updateRows({
        update: [updatedLocation],
        remove: [removedLocation],
      });
    },
    [selectedRows, updateRows]
  );

  const actionButtons = useCallback(
    ({ gridApi }: ITableActionProps) => {
      return [
        <UpsertLocationButton
          key="addLocationButton"
          contactGroupType={contactGroupType}
          onSubmitSuccess={() => gridApi?.refreshServerSide()}
        />,
      ];
    },
    [contactGroupType]
  );

  const selectionActionButtons = useMemo(() => {
    const getContactGroups = (
      selectedRows: IContactGroupAddress[] | IContactGroup[]
    ): Partial<IContactGroup>[] => {
      // Handle IContactGroupAddress[] case
      if (selectedRows.length > 0 && 'contactGroup' in selectedRows[0]) {
        return Array.from(
          new Map(
            (selectedRows as IContactGroupAddress[])
              .filter((row) => row.contactGroup)
              .map((row) => [row.contactGroup.id, row.contactGroup])
          ).values()
        ) as Partial<IContactGroup>[];
      }

      return selectedRows as Partial<IContactGroup>[];
    };

    return [
      <MergeLocationsButton
        key="mergeLocationsButton"
        contactGroupType={contactGroupType}
        onSubmitSuccess={onMergeLocationsSuccess}
      />,
      <MergeContactGroupsButton
        key="mergeContactGroupsButton"
        getContactGroups={getContactGroups}
      />,
      <RemoveLocationButton
        key="removeLocationButton"
        locations={selectedRows}
        onSubmitSuccess={() => updateRows({ remove: selectedRows })}
        notificationPosition={NotificationPosition.TOP}
      />,
    ];
  }, [contactGroupType, onMergeLocationsSuccess, selectedRows, updateRows]);

  return (
    <>
      <AgGridServerTable
        actionButtons={actionButtons}
        selectionActionButtons={selectionActionButtons}
      />

      <ContactGroupDetails
        isOpen={!!selectedContactGroup}
        onClose={() => setSelectedContactGroup(null)}
        contactGroup={selectedContactGroup}
        contactGroupType={contactGroupType}
        defaultTab={ContactGroupDetailsTab.LOCATIONS}
        onEditContactGroupSuccess={onEditContactGroupSuccess}
      />
    </>
  );
};
